import { matchPath } from 'react-router-dom';
import {
  getProjectRouteId,
  resolveProjectSelection,
  type ProjectSelectionCandidate,
} from './projectSelection';

export const orgRouteSegments = {
  docsHome: 'project/:projectId/docs',
  docsFolder: 'project/:projectId/docs/folder/:folderId',
  document: 'project/:projectId/document/:documentId',
  // The Wiki: a typed page by issue key or item id, a type page, every page as
  // a list ("All pages"), and the marks list. Typed-page and type links in page
  // content are written in these shapes (`consoleLinks.ts`).
  pageItem: 'project/:projectId/page/item/:itemRef',
  pageType: 'project/:projectId/page/type/:typeId',
  // Search (`?q=` and `?filters=`) and the map of the project's types.
  wiki: 'project/:projectId/wiki',
  wikiTypes: 'project/:projectId/wiki/types',
  // Older addresses, all shared as links, that redirect (`legacyWikiRedirectPath`):
  // the Wiki's own while it was called Pages, then the retired v1 wiki's shapes
  // that the routes above do not claim (its `/wiki` home and `/wiki/types` map
  // now mean the Wiki's Search and Types), then the v1 wiki inside Docs.
  legacyPages: 'project/:projectId/pages',
  legacyPagesTypes: 'project/:projectId/pages/types',
  retiredWikiPage: 'project/:projectId/wiki/:itemId',
  retiredWikiChanges: 'project/:projectId/wiki/changes',
  retiredWikiChangeset: 'project/:projectId/wiki/changes/:changesetId',
  retiredWikiType: 'project/:projectId/wiki/types/:labelId',
  retiredDocsWikiHome: 'project/:projectId/docs/wiki',
  retiredDocsWikiPage: 'project/:projectId/docs/wiki/:itemId',
  marks: 'project/:projectId/marks',
  trackersHome: 'project/:projectId/trackers',
  trackersType: 'project/:projectId/trackers/type/:trackerType',
  trackerItem: 'project/:projectId/trackers/item/:itemId',
  // Schema screens for the project's trackers; the Ontology inspector is the first tab.
  trackerSetup: 'project/:projectId/trackers/setup',
  trackerSetupTab: 'project/:projectId/trackers/setup/:tab',
  requests: 'requests',
  requestActivity: 'requests/activity/:deliveryId',
  feedbackRequest: 'feedback/:requestId',
  adminRoot: 'admin',
  adminBilling: 'billing',
  adminMembers: 'members',
  adminSecurity: 'security',
  adminDanger: 'danger',
} as const;

export const consoleRoutePatterns = {
  authenticate: '/authenticate',
  orgRoot: '/org/:orgSlug',
  docsHome: `/org/:orgSlug/${orgRouteSegments.docsHome}`,
  docsFolder: `/org/:orgSlug/${orgRouteSegments.docsFolder}`,
  document: `/org/:orgSlug/${orgRouteSegments.document}`,
  pageItem: `/org/:orgSlug/${orgRouteSegments.pageItem}`,
  pageType: `/org/:orgSlug/${orgRouteSegments.pageType}`,
  wiki: `/org/:orgSlug/${orgRouteSegments.wiki}`,
  wikiTypes: `/org/:orgSlug/${orgRouteSegments.wikiTypes}`,
  legacyPages: `/org/:orgSlug/${orgRouteSegments.legacyPages}`,
  legacyPagesTypes: `/org/:orgSlug/${orgRouteSegments.legacyPagesTypes}`,
  retiredWikiPage: `/org/:orgSlug/${orgRouteSegments.retiredWikiPage}`,
  retiredWikiChanges: `/org/:orgSlug/${orgRouteSegments.retiredWikiChanges}`,
  retiredWikiChangeset: `/org/:orgSlug/${orgRouteSegments.retiredWikiChangeset}`,
  retiredWikiType: `/org/:orgSlug/${orgRouteSegments.retiredWikiType}`,
  retiredDocsWikiHome: `/org/:orgSlug/${orgRouteSegments.retiredDocsWikiHome}`,
  retiredDocsWikiPage: `/org/:orgSlug/${orgRouteSegments.retiredDocsWikiPage}`,
  marks: `/org/:orgSlug/${orgRouteSegments.marks}`,
  trackersHome: `/org/:orgSlug/${orgRouteSegments.trackersHome}`,
  trackersType: `/org/:orgSlug/${orgRouteSegments.trackersType}`,
  trackerItem: `/org/:orgSlug/${orgRouteSegments.trackerItem}`,
  trackerSetup: `/org/:orgSlug/${orgRouteSegments.trackerSetup}`,
  trackerSetupTab: `/org/:orgSlug/${orgRouteSegments.trackerSetupTab}`,
  requests: `/org/:orgSlug/${orgRouteSegments.requests}`,
  requestActivity: `/org/:orgSlug/${orgRouteSegments.requestActivity}`,
  feedbackRequest: `/org/:orgSlug/${orgRouteSegments.feedbackRequest}`,
  adminRoot: `/org/:orgSlug/${orgRouteSegments.adminRoot}`,
  adminSection: `/org/:orgSlug/${orgRouteSegments.adminRoot}/:section`,
} as const;

export type AdminSection = 'overview' | 'billing' | 'members' | 'security' | 'danger';

/** Tabs of the Tracker setup screen. `/trackers/setup` with no tab is the first. */
export type TrackerSetupTab = 'ontology' | 'public';
const trackerSetupTabs = new Set<TrackerSetupTab>(['ontology', 'public']);

export type ConsoleRoute =
  | { kind: 'authenticate' }
  | { kind: 'root' }
  | { kind: 'org-root'; orgSlug: string }
  | { kind: 'docs-home'; orgSlug: string; projectId: string }
  | { kind: 'docs-folder'; orgSlug: string; projectId: string; folderId: string }
  | { kind: 'document'; orgSlug: string; projectId: string; documentId: string }
  | { kind: 'page-item'; orgSlug: string; projectId: string; itemRef: string }
  | { kind: 'page-type'; orgSlug: string; projectId: string; typeId: string }
  | { kind: 'wiki'; orgSlug: string; projectId: string }
  | { kind: 'wiki-types'; orgSlug: string; projectId: string }
  | { kind: 'marks'; orgSlug: string; projectId: string }
  | { kind: 'trackers-home'; orgSlug: string; projectId: string }
  | { kind: 'trackers-type'; orgSlug: string; projectId: string; trackerType: string }
  | { kind: 'tracker-item'; orgSlug: string; projectId: string; itemId: string }
  | { kind: 'tracker-setup'; orgSlug: string; projectId: string; tab: TrackerSetupTab | 'unknown' }
  | { kind: 'requests'; orgSlug: string }
  | { kind: 'requestActivity'; orgSlug: string; deliveryId: string }
  | { kind: 'feedbackRequest'; orgSlug: string; requestId: string }
  | { kind: 'admin'; orgSlug: string; section: AdminSection | 'unknown' }
  | { kind: 'unknown' };

export interface RouteTeam {
  orgId: string;
  /** Optional future server-provided canonical slug. */
  orgSlug?: string | null;
  projects?: readonly ProjectSelectionCandidate[];
}

const adminSections = new Set<AdminSection>(['overview', 'billing', 'members', 'security', 'danger']);

function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

/** Stable today because listTeams exposes orgId but not a canonical org slug. */
export function getOrgRouteKey(team: RouteTeam): string {
  return team.orgSlug || team.orgId;
}

export function findTeamByRouteKey<T extends RouteTeam>(teams: T[], routeKey: string): T | null {
  return teams.find(team => team.orgId === routeKey || team.orgSlug === routeKey) || null;
}

export function orgRootPath(team: RouteTeam): string {
  return `/org/${encodeSegment(getOrgRouteKey(team))}`;
}

export function orgDocsPath(team: RouteTeam, projectId: string): string {
  return `${orgRootPath(team)}/project/${encodeSegment(projectId)}/docs`;
}

export function orgFolderPath(team: RouteTeam, projectId: string, folderId: string): string {
  return `${orgDocsPath(team, projectId)}/folder/${encodeSegment(folderId)}`;
}

/**
 * Links into the Wiki from before it had its own addresses: the same item as a
 * typed page, or the matching Wiki view, keeping the query and hash; null for
 * anything else. Segments are carried through as they were encoded.
 *
 * - `/pages` and `/pages/types`: the Wiki while it was called Pages.
 * - `/wiki/:itemId` and `/docs/wiki/:itemId`: a retired v1 wiki page, the same
 *   item as a typed page.
 * - `/wiki/changes[/:id]`, `/wiki/types/:labelId`, `/docs/wiki`: v1 views with
 *   no counterpart, to the Wiki's Search or Types.
 *
 * The v1 wiki's `/wiki` home and `/wiki/types` map collide with the Wiki's
 * own addresses and are not here: those now mean Search and Types.
 */
export function legacyWikiRedirectPath(pathname: string, suffix: string): string | null {
  const match = (...kinds: (keyof typeof consoleRoutePatterns)[]) =>
    kinds.some((kind) => matchPath(consoleRoutePatterns[kind], pathname));
  const project = /^\/org\/[^/]+\/project\/[^/]+/.exec(pathname)?.[0];
  if (!project) return null;
  if (match('legacyPagesTypes', 'retiredWikiType')) return `${project}/wiki/types${suffix}`;
  if (match('legacyPages', 'retiredDocsWikiHome', 'retiredWikiChanges', 'retiredWikiChangeset')) return `${project}/wiki${suffix}`;
  const page = matchPath(consoleRoutePatterns.retiredDocsWikiPage, pathname)
    ?? matchPath(consoleRoutePatterns.retiredWikiPage, pathname);
  const itemId = page?.params.itemId;
  // `/wiki/types` is the Wiki's Types, not a page called "types".
  if (!itemId || (itemId === 'types' && !pathname.includes('/docs/wiki/'))) return null;
  return `${project}/page/item/${encodeSegment(itemId)}${suffix}`;
}

/**
 * The paths below are the retired v1 wiki's own shapes. Its components still
 * render the public wiki, which maps every path under `orgWikiPath` onto
 * `/public/wiki/<slug>`; in the member console they redirect
 * (`legacyWikiRedirectPath`).
 */
export function orgWikiPagePath(team: RouteTeam, projectId: string, itemId: string): string {
  return `${orgWikiPath(team, projectId)}/${encodeSegment(itemId)}`;
}

/** The v1 wiki's changesets; only the GitHub-native wiki and its visual harness route these. */
export function orgWikiChangesPath(team: RouteTeam, projectId: string): string {
  return `${orgWikiPath(team, projectId)}/changes`;
}

/** Pages that carry no label, on the v1 wiki Types section's triage page. */
export const WIKI_UNLABELED_TYPE = '_unlabeled';

/** One label's v1 type page: its properties, health and instance table. */
export function orgWikiTypePath(team: RouteTeam, projectId: string, labelId: string): string {
  return `${orgWikiTypesPath(team, projectId)}/${encodeSegment(labelId)}`;
}

export function orgWikiChangesetPath(team: RouteTeam, projectId: string, changesetId: string): string {
  return `${orgWikiChangesPath(team, projectId)}/${encodeSegment(changesetId)}`;
}

export function orgDocumentPath(team: RouteTeam, projectId: string, documentId: string, anchor?: { blockId?: string; threadId?: string; commentId?: string }): string {
  const params = new URLSearchParams();
  for (const key of ['blockId', 'threadId', 'commentId'] as const) {
    if (anchor?.[key]) params.set(key, anchor[key]);
  }
  const query = params.toString();
  return `/org/${encodeSegment(getOrgRouteKey(team))}/project/${encodeSegment(projectId)}/document/${encodeSegment(documentId)}${query ? `?${query}` : ''}`;
}

function projectRoot(team: RouteTeam, projectId: string): string {
  return `${orgRootPath(team)}/project/${encodeSegment(projectId)}`;
}

/** A typed page in the Wiki; `itemRef` is its issue key or item id. */
export function orgPageItemPath(team: RouteTeam, projectId: string, itemRef: string): string {
  return `${projectRoot(team, projectId)}/page/item/${encodeSegment(itemRef)}`;
}

/** A type's page in the Wiki: its prose above a table of its items. */
export function orgTypePagePath(team: RouteTeam, projectId: string, typeId: string): string {
  return `${projectRoot(team, projectId)}/page/type/${encodeSegment(typeId)}`;
}

/** The Wiki's Search: every page of the project as a table; `search` is a `pagesSearchQuery` string (filters and text). */
export function orgWikiPath(team: RouteTeam, projectId: string, search = ''): string {
  return `${projectRoot(team, projectId)}/wiki${search ? `?${search}` : ''}`;
}

/** The map of the project's page types. */
export function orgWikiTypesPath(team: RouteTeam, projectId: string): string {
  return `${projectRoot(team, projectId)}/wiki/types`;
}

/** The project's decisions and open questions, across every page. */
export function orgMarksPath(team: RouteTeam, projectId: string, marks: 'all' | 'decided' | 'open' = 'all'): string {
  const base = `${projectRoot(team, projectId)}/marks`;
  return marks === 'all' ? base : `${base}?kind=${marks}`;
}

/**
 * The Trackers surface for a project.
 *
 * A saved view rides as `?view=<id>` rather than as a path segment: a view
 * carries its own type selection, so it is a lens over the surface and not a
 * place inside it. That also keeps a shared view link stable when the view is
 * later renamed.
 */
export function orgTrackersPath(team: RouteTeam, projectId: string, viewId?: string | null): string {
  const base = `${orgRootPath(team)}/project/${encodeSegment(projectId)}/trackers`;
  return viewId ? `${base}?view=${encodeURIComponent(viewId)}` : base;
}

/** The project's Tracker setup screen: its schema, starting with the Ontology inspector. */
export function orgTrackerSetupPath(team: RouteTeam, projectId: string, tab?: TrackerSetupTab): string {
  const base = `${orgRootPath(team)}/project/${encodeSegment(projectId)}/trackers/setup`;
  return tab ? `${base}/${encodeSegment(tab)}` : base;
}

export function orgTrackerTypePath(team: RouteTeam, projectId: string, trackerType: string): string {
  return `${orgRootPath(team)}/project/${encodeSegment(projectId)}/trackers/type/${encodeSegment(trackerType)}`;
}

/**
 * One item, open beside the rows it came from.
 *
 * The list context rides as a query rather than as path segments, for the same
 * reason `?view=` does above: an item is one place, not one place per lens onto
 * it, and a pasted link with no query is still a valid address -- it opens the
 * item against the unfiltered list. Carrying it matters because the list is now
 * on screen next to the item: without it, opening a bug from the Bugs list
 * would silently reset the rows beside it to every tracker in the project.
 */
export function orgTrackerItemPath(
  team: RouteTeam,
  projectId: string,
  itemId: string,
  listContext?: { trackerType?: string | null; viewId?: string | null },
): string {
  const base = `${orgRootPath(team)}/project/${encodeSegment(projectId)}/trackers/item/${encodeSegment(itemId)}`;
  // A saved view already names its own type, so the two never both apply.
  if (listContext?.viewId) return `${base}?view=${encodeURIComponent(listContext.viewId)}`;
  const trackerType = listContext?.trackerType;
  if (trackerType) return `${base}?type=${encodeURIComponent(trackerType)}`;
  return base;
}

/** The Requests surface with nothing selected. */
export function orgRequestsPath(team: RouteTeam): string {
  return `${orgRootPath(team)}/${orgRouteSegments.requests}`;
}

/**
 * The pasteable link an author hands a recipient; see `sanitizeReturnPath`.
 *
 * It lands *inside* the Requests surface with this row selected rather than on
 * a standalone page — the same decision the desktop made in S4 — so a recipient
 * who came for one request can see the others waiting on them.
 */
export function orgFeedbackRequestPath(team: RouteTeam, requestId: string): string {
  return `${orgRootPath(team)}/feedback/${encodeSegment(requestId)}`;
}

/**
 * One inbox activity -- a mention, a reply, a decision -- open in the content
 * column beside the list it came from, the same arrangement a feedback request
 * gets. Addressed by delivery because the row is what the reader chose; where
 * inside the document it points (the thread, the comment) rides in the query
 * exactly as it does on a document link, so the anchor code is shared.
 */
export function orgRequestActivityPath(
  team: RouteTeam,
  deliveryId: string,
  anchor?: { blockId?: string; threadId?: string; commentId?: string },
): string {
  const params = new URLSearchParams();
  for (const key of ['blockId', 'threadId', 'commentId'] as const) {
    if (anchor?.[key]) params.set(key, anchor[key]);
  }
  const query = params.toString();
  return `${orgRequestsPath(team)}/activity/${encodeSegment(deliveryId)}${query ? `?${query}` : ''}`;
}

export function orgAdminPath(team: RouteTeam, section: AdminSection = 'overview'): string {
  const root = `/org/${encodeSegment(getOrgRouteKey(team))}/admin`;
  return section === 'overview' ? root : `${root}/${section}`;
}

export function resolveConsoleRoute(pathname: string): ConsoleRoute {
  if (pathname === '/') return { kind: 'root' };
  if (matchPath({ path: consoleRoutePatterns.authenticate, end: true }, pathname)) {
    return { kind: 'authenticate' };
  }

  const document = matchPath(consoleRoutePatterns.document, pathname);
  if (document?.params.orgSlug && document.params.projectId && document.params.documentId) {
    return {
      kind: 'document',
      orgSlug: document.params.orgSlug,
      projectId: document.params.projectId,
      documentId: document.params.documentId,
    };
  }

  // Links from before the Wiki had its own addresses resolve as where they
  // redirect; the old `/docs/wiki` ones must be matched before the docs home.
  const legacyWiki = legacyWikiRedirectPath(pathname, '');
  if (legacyWiki) return resolveConsoleRoute(legacyWiki);

  for (const kind of ['pageItem', 'pageType', 'wiki', 'wikiTypes', 'marks'] as const) {
    const match = matchPath(consoleRoutePatterns[kind], pathname);
    const params = match?.params as Record<string, string | undefined> | undefined;
    if (!params?.orgSlug || !params.projectId) continue;
    const base = { orgSlug: params.orgSlug, projectId: params.projectId };
    if (kind === 'pageItem' && params.itemRef) return { kind: 'page-item', ...base, itemRef: params.itemRef };
    if (kind === 'pageType' && params.typeId) return { kind: 'page-type', ...base, typeId: params.typeId };
    if (kind === 'wiki') return { kind: 'wiki', ...base };
    if (kind === 'wikiTypes') return { kind: 'wiki-types', ...base };
    if (kind === 'marks') return { kind: 'marks', ...base };
  }

  const folder = matchPath(consoleRoutePatterns.docsFolder, pathname);
  if (folder?.params.orgSlug && folder.params.projectId && folder.params.folderId) {
    return {
      kind: 'docs-folder',
      orgSlug: folder.params.orgSlug,
      projectId: folder.params.projectId,
      folderId: folder.params.folderId,
    };
  }

  const docs = matchPath(consoleRoutePatterns.docsHome, pathname);
  if (docs?.params.orgSlug && docs.params.projectId) {
    return { kind: 'docs-home', orgSlug: docs.params.orgSlug, projectId: docs.params.projectId };
  }

  // Item and type before home: all three share the `/trackers` prefix, and
  // `matchPath` is exact, but keeping the specific ones first makes the
  // precedence readable rather than incidental.
  const trackerItem = matchPath(consoleRoutePatterns.trackerItem, pathname);
  if (trackerItem?.params.orgSlug && trackerItem.params.projectId && trackerItem.params.itemId) {
    return {
      kind: 'tracker-item',
      orgSlug: trackerItem.params.orgSlug,
      projectId: trackerItem.params.projectId,
      itemId: trackerItem.params.itemId,
    };
  }

  const trackerSetup = matchPath(consoleRoutePatterns.trackerSetupTab, pathname) ?? matchPath(consoleRoutePatterns.trackerSetup, pathname);
  if (trackerSetup?.params.orgSlug && trackerSetup.params.projectId) {
    const tab = (trackerSetup.params as { tab?: string }).tab ?? 'ontology';
    return {
      kind: 'tracker-setup',
      orgSlug: trackerSetup.params.orgSlug,
      projectId: trackerSetup.params.projectId,
      tab: trackerSetupTabs.has(tab as TrackerSetupTab) ? tab as TrackerSetupTab : 'unknown',
    };
  }

  const trackersType = matchPath(consoleRoutePatterns.trackersType, pathname);
  if (trackersType?.params.orgSlug && trackersType.params.projectId && trackersType.params.trackerType) {
    return {
      kind: 'trackers-type',
      orgSlug: trackersType.params.orgSlug,
      projectId: trackersType.params.projectId,
      trackerType: trackersType.params.trackerType,
    };
  }

  const trackersHome = matchPath(consoleRoutePatterns.trackersHome, pathname);
  if (trackersHome?.params.orgSlug && trackersHome.params.projectId) {
    return {
      kind: 'trackers-home',
      orgSlug: trackersHome.params.orgSlug,
      projectId: trackersHome.params.projectId,
    };
  }

  const feedbackRequest = matchPath(consoleRoutePatterns.feedbackRequest, pathname);
  if (feedbackRequest?.params.orgSlug && feedbackRequest.params.requestId) {
    return {
      kind: 'feedbackRequest',
      orgSlug: feedbackRequest.params.orgSlug,
      requestId: feedbackRequest.params.requestId,
    };
  }

  const requestActivity = matchPath(consoleRoutePatterns.requestActivity, pathname);
  if (requestActivity?.params.orgSlug && requestActivity.params.deliveryId) {
    return {
      kind: 'requestActivity',
      orgSlug: requestActivity.params.orgSlug,
      deliveryId: requestActivity.params.deliveryId,
    };
  }

  const requests = matchPath(consoleRoutePatterns.requests, pathname);
  if (requests?.params.orgSlug) return { kind: 'requests', orgSlug: requests.params.orgSlug };

  const adminSection = matchPath(consoleRoutePatterns.adminSection, pathname);
  if (adminSection?.params.orgSlug && adminSection.params.section) {
    const section = adminSections.has(adminSection.params.section as AdminSection)
      ? adminSection.params.section as AdminSection
      : 'unknown';
    return { kind: 'admin', orgSlug: adminSection.params.orgSlug, section };
  }

  const admin = matchPath(consoleRoutePatterns.adminRoot, pathname);
  if (admin?.params.orgSlug) return { kind: 'admin', orgSlug: admin.params.orgSlug, section: 'overview' };

  const org = matchPath(consoleRoutePatterns.orgRoot, pathname);
  if (org?.params.orgSlug) return { kind: 'org-root', orgSlug: org.params.orgSlug };
  return { kind: 'unknown' };
}

/**
 * The console's modes, each with one gutter item.
 *
 * `trackers` is the one addition trackers-in-the-console makes, peer to Shared
 * Docs the way the desktop keeps trackers and documents as separate modes. It
 * is the last item this workstream adds: the surface grows through the saved
 * views and the type tree inside it, not through new icons out here.
 */
export type ConsoleMode = 'docs' | 'trackers' | 'organization' | 'requests';

/**
 * Which gutter item a route lights up, and which layout wraps it.
 *
 * Here rather than inline in the shell so a pasted deep link and the list it
 * lands in are provably the same mode.
 */
export function consoleModeForRoute(route: ConsoleRoute): ConsoleMode {
  if (route.kind === 'admin') return 'organization';
  if (route.kind === 'requests' || route.kind === 'requestActivity' || route.kind === 'feedbackRequest') return 'requests';
  if (route.kind === 'trackers-home' || route.kind === 'trackers-type' || route.kind === 'tracker-item' || route.kind === 'tracker-setup') {
    return 'trackers';
  }
  return 'docs';
}

export interface LegacyRedirectInput {
  pathname: string;
  search: string;
  teams: RouteTeam[];
  activeOrgId?: string | null;
  selectedProjectId?: string | null;
}

const legacyDocsHomePattern = '/org/:orgSlug/docs';
const legacyDocsFolderPattern = '/org/:orgSlug/docs/folder/:folderId';

/** Map query-selected roots and old unscoped docs URLs into a project route. */
export function resolveLegacyRedirect({
  pathname,
  search,
  teams,
  activeOrgId,
  selectedProjectId,
}: LegacyRedirectInput): string | null {
  if (teams.length === 0) return null;

  if (pathname === '/') {
    const query = new URLSearchParams(search);
    const requestedOrgId = query.get('orgId');
    const team = (requestedOrgId && teams.find(candidate => candidate.orgId === requestedOrgId))
      || (activeOrgId && teams.find(candidate => candidate.orgId === activeOrgId))
      || teams[0];
    if (!team) return null;
    const project = resolveProjectSelection(team.projects ?? [], undefined, selectedProjectId);
    const nextQuery = new URLSearchParams();
    if (query.get('invitation') === 'accepted') nextQuery.set('invitation', 'accepted');
    const suffix = nextQuery.size > 0 ? `?${nextQuery.toString()}` : '';
    // With no visible projects, retain the selected organization so its empty
    // state can render; there is no project-scoped docs URL we can truthfully mint.
    if (!project) return `${orgRootPath(team)}${suffix}`;
    return `${orgDocsPath(team, getProjectRouteId(project))}${suffix}`;
  }

  const legacyFolder = matchPath(legacyDocsFolderPattern, pathname);
  const legacyHome = legacyFolder ? null : matchPath(legacyDocsHomePattern, pathname);
  const orgSlug = legacyFolder?.params.orgSlug ?? legacyHome?.params.orgSlug;
  if (!orgSlug) return null;
  const team = findTeamByRouteKey(teams, orgSlug);
  if (!team) return null;
  const project = resolveProjectSelection(team.projects ?? [], undefined, selectedProjectId);
  if (!project) return `${orgRootPath(team)}${search}`;
  const routeProjectId = getProjectRouteId(project);
  const path = legacyFolder?.params.folderId
    ? orgFolderPath(team, routeProjectId, legacyFolder.params.folderId)
    : orgDocsPath(team, routeProjectId);
  return `${path}${search}`;
}

/**
 * Only same-origin SPA paths are eligible as post-authentication return paths.
 *
 * Deliberately a rule rather than a route allowlist. A feedback cold link is
 * the console's first entry point that a signed-out stranger clicks, and an
 * allowlist that silently forgot it would drop the recipient on the docs home
 * with nothing on screen to say why.
 *
 * Twin of `collabv3/src/webInviteReturnPath.ts`
 * `normalizeWebInviteReturnPath`; change both together.
 */
export function sanitizeReturnPath(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  try {
    const url = new URL(value, 'https://console.nimbalyst.com');
    if (url.origin !== 'https://console.nimbalyst.com' || url.pathname === '/authenticate') return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}
